import { Box, Button, Flex, Input, Text } from '@chakra-ui/react';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useCountdownTimer } from '../../../../../../hooks';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../../constants/recaptchable';
import authenticationService from '../../../../../../service/authenticationService';
import { createDeferredPromise } from '../../../../../../utils/promise';
import shoplineCaptcha from '../../../../../../utils/shoplineCaptcha';
import { useAppContext } from '../../../../context/appContext';
import { MOBILE_PHONE_STEP } from '../../../../constants';
import { FIELD } from '../../constants/field';
import { useProfileContext } from '../../context/profileContext';
import { guardApiWithCaptcha } from '../../../../../../customElements/sl_armor_captcha/utils/armorCaptchaGuard';
import { ARMOR_CAPTCHA_SCENES } from '../../../../../../customElements/sl_armor_captcha';

const DEFAULT_MOBILE_CODE_FORM_VALUE = {
  submitting: false,
  code: '',
};

const RESEND_COUNT_DOWN_MAX = 60;
const RESEND_COUNT_DOWN_MIN = 0;

const SEND_CODE_BUTTON_ID = 'member-center-profile-confirm-send-code_button';
const RESEND_CODE_BUTTON_ID = 'member-center-profile-resend-code_button';

const MobileCheckProcess = ({
  step,
  setStep,
  onVerifiedSuccess,
  setErrorMessages,
}) => {
  const { t } = useTranslation();
  const { grecaptcha, recaptchaSiteKey, slFeatureService } = useAppContext();
  const { getUserInfo } = useProfileContext();
  const [mobileCodeForm, setMobileCodeForm] = useState(
    DEFAULT_MOBILE_CODE_FORM_VALUE,
  );
  const shoplineCaptchaPromiseRef = useRef(createDeferredPromise());
  const { watch } = useFormContext();
  const {
    timeLeft,
    start: startTimer,
    reset: resetTimer,
    isRunning: isTimerRunning,
    hasFinished,
  } = useCountdownTimer({
    defaultSeconds: RESEND_COUNT_DOWN_MAX,
  });
  const refReCAPTCHA = useRef();
  const hasShoplineCaptcha = slFeatureService.hasFeature('shopline_captcha');

  const {
    [FIELD.MOBILE_PHONE]: mobilePhone,
    [FIELD.COUNTRY_CALLING_CODE]: countryCallingCode,
  } = watch([FIELD.MOBILE_PHONE, FIELD.COUNTRY_CALLING_CODE]);

  const sendCode = useCallback(async () => {
    try {
      const payload = {
        country_calling_code: countryCallingCode,
        mobile_phone: mobilePhone,
      };
      payload[RECAPTCHA_TOKEN_FIELD] =
        await refReCAPTCHA.current?.executeAsync?.();
      refReCAPTCHA.current?.reset?.();
      if (hasShoplineCaptcha) {
        payload.m_token = await shoplineCaptchaPromiseRef.current.promise;
        payload.dfp_token = await shoplineCaptcha.getTokenAsync();
      }
      setMobileCodeForm((prev) => ({ ...prev, submitting: true }));
      setErrorMessages([]);
      await guardApiWithCaptcha({
        scene: ARMOR_CAPTCHA_SCENES.PROFILE_UPDATE,
        request: (extra) =>
          authenticationService.sendMobileChangeCode({ ...payload, ...extra }),
      });
      setMobileCodeForm((prev) => ({ ...prev, submitting: false }));
      setStep(MOBILE_PHONE_STEP.ENTER_CODE);
      startTimer();
    } catch (error) {
      console.error(error);
      setMobileCodeForm((prev) => ({ ...prev, submitting: false }));
      setErrorMessages([
        error?.response?.data?.error_messages ??
          error?.response?.data?.error?.message ??
          error?.response?.data?.error,
      ]);
    } finally {
      shoplineCaptchaPromiseRef.current = createDeferredPromise();
    }
  }, [
    countryCallingCode,
    hasShoplineCaptcha,
    mobilePhone,
    setErrorMessages,
    setStep,
    startTimer,
  ]);

  const formSubmit = useCallback(async () => {
    if (!mobileCodeForm.code || typeof getUserInfo !== 'function') {
      return;
    }
    setMobileCodeForm((prev) => ({ ...prev, submitting: true }));
    setErrorMessages([]);
    const params = {
      country_calling_code: countryCallingCode,
      mobile_phone: mobilePhone,
      code: mobileCodeForm.code,
    };

    try {
      await authenticationService.verifyCode(params);
      setMobileCodeForm((prev) => ({ ...prev, submitting: false }));
      setStep(MOBILE_PHONE_STEP.EMPTY);
      await getUserInfo({ isBackgroundSync: true });
      onVerifiedSuccess();
    } catch (error) {
      console.error(error);
      setMobileCodeForm((prev) => ({ ...prev, submitting: false }));
      setErrorMessages([
        error?.response?.data?.error_messages ?? error?.response?.status,
      ]);
    }
  }, [
    mobileCodeForm.code,
    getUserInfo,
    setErrorMessages,
    countryCallingCode,
    mobilePhone,
    setStep,
    onVerifiedSuccess,
  ]);

  const goBack = useCallback(
    (e) => {
      if (!mobileCodeForm.submitting) {
        e.preventDefault();
        setErrorMessages([]);
        setStep(MOBILE_PHONE_STEP.EMPTY);
        resetTimer();
      }
    },
    [mobileCodeForm.submitting, resetTimer, setErrorMessages, setStep],
  );

  const handleCodeInputChange = useCallback((e) => {
    e.persist();
    setMobileCodeForm((prev) => ({ ...prev, code: e.target?.value }));
  }, []);

  useEffect(() => {
    if (!hasShoplineCaptcha) {
      return;
    }

    let sendCodeListener = null;
    if (step === MOBILE_PHONE_STEP.CHECK_MOBILE) {
      sendCodeListener = shoplineCaptcha.getListener(
        SEND_CODE_BUTTON_ID,
        (shoplineCaptchaMToken) =>
          shoplineCaptchaPromiseRef.current.resolve(shoplineCaptchaMToken),
      );
      return () => sendCodeListener?.destroy?.();
    }

    if (step === MOBILE_PHONE_STEP.ENTER_CODE && hasFinished) {
      sendCodeListener = shoplineCaptcha.getListener(
        RESEND_CODE_BUTTON_ID,
        (shoplineCaptchaMToken) =>
          shoplineCaptchaPromiseRef.current.resolve(shoplineCaptchaMToken),
      );
      return () => sendCodeListener?.destroy?.();
    }
  }, [hasShoplineCaptcha, step, hasFinished]);

  return (
    <div>
      {step === MOBILE_PHONE_STEP.CHECK_MOBILE && (
        <Text
          variant="description"
          className="member-center-profile__description-text member-center-profile__mobile-check-text"
        >
          {t('session.mobile_signup.check_mobile.title')}
        </Text>
      )}
      {step === MOBILE_PHONE_STEP.ENTER_CODE && (
        <Text
          variant="description"
          className="member-center-profile__description-text member-center-profile__mobile-check-text"
        >
          {t('session.mobile_signup.input_code.hint')}
        </Text>
      )}
      {[MOBILE_PHONE_STEP.CHECK_MOBILE, MOBILE_PHONE_STEP.ENTER_CODE].includes(
        step,
      ) && (
        <>
          <Text
            variant="description"
            className="member-center-profile__description-text member-center-profile__mobile-check-text"
            marginTop="40px"
          >
            {t('session.mobile_signup.check_mobile.your_number')}
          </Text>
          <Flex justifyContent="center" alignItems="center" gap="0.25rem">
            <Text
              variant="checkMobileNumber"
              className="member-center-profile__description-text member-center-profile__mobile-check-text"
            >
              +{countryCallingCode}
            </Text>
            <Text
              variant="checkMobileNumber"
              className="member-center-profile__description-text member-center-profile__mobile-check-text"
            >
              {mobilePhone}
            </Text>
          </Flex>
          <ReCAPTCHA
            ref={refReCAPTCHA}
            size="invisible"
            grecaptcha={grecaptcha}
            sitekey={recaptchaSiteKey}
          />
        </>
      )}
      {step === MOBILE_PHONE_STEP.CHECK_MOBILE && (
        <Box marginTop="40px">
          <Button
            data-e2e-id="member-center-profile-confirm-and-send-code_button"
            id={SEND_CODE_BUTTON_ID}
            className="member-center-profile__primary-button"
            variant="fillSuccess"
            isLoading={mobileCodeForm.submitting}
            onClick={sendCode}
            isFullWidth
          >
            {t('session.mobile_signup.check_mobile.send_code')}
          </Button>
          <Box
            marginTop="20px"
            className="member-center-profile__mobile-check-text"
          >
            {t('session.mobile_signup.check_mobile.go_back.1')}
            <Button
              variant="textButton"
              className="member-center-profile__text-button"
              onClick={goBack}
              verticalAlign="baseline"
              data-e2e-id="member-center-profile-check-mobile-go-back_button"
              mx="4px"
            >
              {t('session.mobile_signup.check_mobile.go_back.2')}
            </Button>
            {t('session.mobile_signup.check_mobile.go_back.3')}
          </Box>
        </Box>
      )}
      {step === MOBILE_PHONE_STEP.ENTER_CODE && (
        <Box marginTop="20px">
          <Input
            value={mobileCodeForm.code}
            onChange={handleCodeInputChange}
            data-e2e-id="member-center-profile-send-code_field"
          />
          <Button
            variant="fillSuccess"
            isLoading={mobileCodeForm.submitting}
            disabled={!mobileCodeForm.code}
            isFullWidth
            onClick={formSubmit}
            marginTop="20px"
            data-e2e-id="member-center-profile-verify-code_button"
            className="member-center-profile__primary-button"
          >
            {t('session.mobile_signup.input_code.submit')}
          </Button>
          {((isTimerRunning && timeLeft > RESEND_COUNT_DOWN_MIN) ||
            mobileCodeForm.submitting) && (
            <Button
              variant="textButton"
              className="member-center-profile__text-button countdown-btn"
              disabled
              marginTop="16px"
            >
              {t('session.mobile_signup.input_code.resend.countdown', {
                resendCountdown: timeLeft,
              })}
            </Button>
          )}
          {timeLeft === RESEND_COUNT_DOWN_MIN && (
            <Button
              type="button"
              data-e2e-id="member-center-profile-resend-code_button"
              id={RESEND_CODE_BUTTON_ID}
              variant="textButton"
              className="member-center-profile__text-button member-center-profile__primary-button"
              isLoading={mobileCodeForm.submitting}
              onClick={sendCode}
              marginTop="16px"
            >
              {t('session.mobile_signup.input_code.resend')}
            </Button>
          )}
        </Box>
      )}
    </div>
  );
};

MobileCheckProcess.propTypes = {
  step: PropTypes.string.isRequired,
  setStep: PropTypes.func.isRequired,
  onVerifiedSuccess: PropTypes.func.isRequired,
  setErrorMessages: PropTypes.func.isRequired,
};

export default MobileCheckProcess;
